import React, { ReactElement, ReactNode } from "react";
import Nav from "./nav";
import Footer from "./footer";

interface LayoutProps {
  children: ReactNode;
  headerComponent?: ReactElement;
}

interface LayoutHeaderProps {
  children: ReactNode;
}

interface LayoutBodyProps {
  children: ReactNode;
}

export function LayoutHeader({ children }: Readonly<LayoutHeaderProps>) {
  return (
    <div className="mx-auto max-w-2xl py-4 sm:py-6 lg:py-12">
      <div className="text-center">
        <h1 className="text-4xl font-semibold font-prompt tracking-tight text-balance text-white sm:text-7xl">
          {children}
        </h1>
      </div>
    </div>
  );
}

export function LayoutBody({ children }: Readonly<LayoutBodyProps>) {
  return (
    <div className="bg-blue-50 dark:bg-blue-100 px-0 py-0.5">
      <div className="mx-auto max-w-full lg:max-w-6xl py-4 px-4 bg-white dark:bg-blue-100">
        {children}
      </div>
    </div>
  );
}

export function LayoutBodyWithoutPadding({
  children,
}: Readonly<LayoutBodyProps>) {
  return (
    <div className="bg-blue-50 dark:bg-black px-0 lg:px-4 py-0.5">
      <div className="mx-auto max-w-full lg:max-w-7xl bg-white dark:bg-black-50">
        {children}
      </div>
    </div>
  );
}

export default function Layout({
  children,
  headerComponent,
}: Readonly<LayoutProps>) {
  return (
    <>
      <div className="bg-gray-900">
        <Nav />
        <div className="relative isolate px-6 py-12 lg:px-8 overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
          >
            <div
              style={{
                clipPath:
                  "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
              }}
              className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-288.75"
            ></div>
          </div>
          {headerComponent && <>{headerComponent}</>}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
          >
            <div
              style={{
                clipPath:
                  "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
              }}
              className="relative left-[calc(50%+3rem)] aspect-1155/678 w-144.5 -translate-x-1/2 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-288.75"
            ></div>
          </div>
        </div>
      </div>
      {children}
      <Footer />
    </>
  );
}
